/**
 * QR Router - Shopify 2.0 条件跳转系统
 * 文件：assets/qr-router.js
 * 
 * 功能：根据访客条件（国家、URL参数、登录状态等）自动跳转到目标页面
 * 执行时机：DOMContentLoaded，仅执行一次
 * 安全机制：防死循环、防重复执行
 */

(function () {
  'use strict';

  // ============================================================
  // 防重复执行锁
  // 确保整个 QR Router 系统只运行一次，即使页面中有多个 Section
  // ============================================================
  if (window.__qrRouterExecuted) {
    return;
  }
  window.__qrRouterExecuted = true;

  // ============================================================
  // 工具函数：标准化路径，用于比较当前路径与目标路径
  // 去除末尾斜杠，统一格式，防止 /page 与 /page/ 被判断为不同路径
  // ============================================================
  function normalizePath(url) {
    if (!url) return '';
    try {
      // 如果是完整 URL，提取 pathname
      if (url.startsWith('http://') || url.startsWith('https://')) {
        var parsed = new URL(url);
        return parsed.pathname.replace(/\/$/, '') || '/';
      }
      // 如果是相对路径，去除末尾斜杠
      return url.replace(/\/$/, '') || '/';
    } catch (e) {
      return url.replace(/\/$/, '') || '/';
    }
  }

  // ============================================================
  // 工具函数：安全跳转
  // 检查目标路径是否与当前路径相同，相同则不跳转（防死循环）
  // ============================================================
  function safeRedirect(targetUrl, debugMode, sectionIndex) {
    if (!targetUrl) return false;

    var currentPath = normalizePath(window.location.pathname);
    var targetPath = normalizePath(targetUrl);

    // 防跳转到当前页面（死循环保护）
    if (currentPath === targetPath) {
      if (debugMode) {
        console.log('[QR Router] Section ' + sectionIndex + ': 目标路径与当前路径相同，跳过跳转。目标: ' + targetPath);
      }
      return false;
    }

    if (debugMode) {
      console.log('[QR Router] Section ' + sectionIndex + ': 准备跳转 → ' + targetUrl);
    }

    // 执行跳转
    window.location.href = targetUrl;
    return true;
  }

  // ============================================================
  // 条件判断函数集合
  // 每种条件类型封装为独立函数，便于未来扩展
  // ============================================================

  /**
   * 条件：URL 参数匹配
   * @param {Object} config - { param_name: string, param_value: string }
   * @returns {boolean}
   */
  function checkUrlParam(config) {
    var paramName = (config.param_name || '').trim();
    var paramValue = (config.param_value || '').trim();
    if (!paramName) return false;

    var urlParams = new URLSearchParams(window.location.search);
    var actualValue = urlParams.get(paramName);

    // 如果没有指定期望值，只检查参数是否存在
    if (paramValue === '') {
      return actualValue !== null;
    }
    return actualValue === paramValue;
  }

  /**
   * 条件：国家代码匹配
   * 通过 Shopify 注入的 window.Shopify.country 或 data 属性获取
   * 也支持从 <html lang> 或 meta 标签推断
   * @param {Object} config - { country_code: string }
   * @returns {boolean}
   */
  function checkCountry(config) {
    var expectedCountry = (config.country_code || '').trim().toUpperCase();
    if (!expectedCountry) return false;

    // 方法1：Shopify 全局对象（需 Shopify 注入）
    var shopifyCountry = '';
    if (window.Shopify && window.Shopify.country) {
      shopifyCountry = window.Shopify.country.toUpperCase();
    }

    // 方法2：通过页面 data 属性传入的国家（由 liquid 注入）
    if (!shopifyCountry && window.__qrRouterCountry) {
      shopifyCountry = window.__qrRouterCountry.toUpperCase();
    }

    if (!shopifyCountry) return false;
    return shopifyCountry === expectedCountry;
  }

  /**
   * 条件：登录状态
   * @param {Object} config - { is_logged_in: 'true' | 'false' }
   * @returns {boolean}
   */
  function checkLoginStatus(config) {
    var expectedStatus = (config.is_logged_in || 'false').toString().toLowerCase();
    // window.__qrRouterLoggedIn 由 liquid 通过 Shopify customer 对象注入
    var actualStatus = window.__qrRouterLoggedIn ? 'true' : 'false';
    return actualStatus === expectedStatus;
  }

  /**
   * 条件：客户标签匹配
   * @param {Object} config - { customer_tag: string }
   * @returns {boolean}
   */
  function checkCustomerTag(config) {
    var expectedTag = (config.customer_tag || '').trim().toLowerCase();
    if (!expectedTag) return false;

    // window.__qrRouterCustomerTags 由 liquid 注入，格式为逗号分隔字符串或数组
    var tags = window.__qrRouterCustomerTags || '';
    if (typeof tags === 'string') {
      tags = tags.split(',').map(function (t) { return t.trim().toLowerCase(); });
    }
    return tags.indexOf(expectedTag) !== -1;
  }

  /**
   * 条件：时间段匹配（使用浏览器本地时间）
   * @param {Object} config - { start_time: string, end_time: string }
   *   时间格式：'HH:MM'（24小时制）
   * @returns {boolean}
   */
  function checkTimeRange(config) {
    var startTime = (config.start_time || '').trim();
    var endTime = (config.end_time || '').trim();
    if (!startTime || !endTime) return false;

    var now = new Date();
    var currentMinutes = now.getHours() * 60 + now.getMinutes();

    var startParts = startTime.split(':');
    var endParts = endTime.split(':');
    var startMinutes = parseInt(startParts[0], 10) * 60 + parseInt(startParts[1] || 0, 10);
    var endMinutes = parseInt(endParts[0], 10) * 60 + parseInt(endParts[1] || 0, 10);

    // 支持跨午夜时间段，例如 22:00 ~ 02:00
    if (startMinutes <= endMinutes) {
      return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
    } else {
      // 跨午夜
      return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
    }
  }

  /**
   * 条件：来源 URL 包含指定字符串
   * @param {Object} config - { referrer_contains: string }
   * @returns {boolean}
   */
  function checkReferrer(config) {
    var expectedStr = (config.referrer_contains || '').trim().toLowerCase();
    if (!expectedStr) return false;
    var referrer = document.referrer.toLowerCase();
    return referrer.indexOf(expectedStr) !== -1;
  }

  /**
   * 条件：设备类型匹配
   * @param {Object} config - { device_type: 'mobile' | 'desktop' }
   * @returns {boolean}
   */
  function checkDeviceType(config) {
    var expectedType = (config.device_type || '').trim().toLowerCase();
    if (!expectedType) return false;

    var isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    var actualType = isMobile ? 'mobile' : 'desktop';
    return actualType === expectedType;
  }

  // ============================================================
  // 条件调度器：根据 block type 调用对应条件函数
  // 如需扩展新条件类型，在此添加新的 case 即可
  // ============================================================
  function evaluateCondition(blockType, blockConfig, debugMode, sectionIndex, blockIndex) {
    var result = false;

    switch (blockType) {
      case 'url_param':
        result = checkUrlParam(blockConfig);
        break;
      case 'country':
        result = checkCountry(blockConfig);
        break;
      case 'login_status':
        result = checkLoginStatus(blockConfig);
        break;
      case 'customer_tag':
        result = checkCustomerTag(blockConfig);
        break;
      case 'time_range':
        result = checkTimeRange(blockConfig);
        break;
      case 'referrer':
        result = checkReferrer(blockConfig);
        break;
      case 'device_type':
        result = checkDeviceType(blockConfig);
        break;
      default:
        if (debugMode) {
          console.warn('[QR Router] Section ' + sectionIndex + ' Block ' + blockIndex + ': 未知条件类型 "' + blockType + '"');
        }
        result = false;
    }

    if (debugMode) {
      console.log(
        '[QR Router] Section ' + sectionIndex + ' Block ' + blockIndex +
        ' [' + blockType + ']：' + JSON.stringify(blockConfig) +
        ' → ' + (result ? '✅ 满足' : '❌ 不满足')
      );
    }

    return result;
  }

  // ============================================================
  // 核心执行函数：处理单个 QR Router Section
  // @param {Element} sectionEl - Section DOM 元素
  // @param {number} sectionIndex - Section 在页面中的序号（调试用）
  // @returns {boolean} - 是否触发了跳转
  // ============================================================
  function processSection(sectionEl, sectionIndex) {
    // 读取 Section 配置（通过 data 属性传入）
    var dataset = sectionEl.dataset;

    // 检查是否启用
    if (dataset.enableRouter !== 'true') {
      if (dataset.debugMode === 'true') {
        console.log('[QR Router] Section ' + sectionIndex + ': 已禁用，跳过。');
      }
      return false;
    }

    var debugMode = dataset.debugMode === 'true';
    var targetUrl = dataset.targetUrl || '';
    var defaultUrl = dataset.defaultUrl || '';
    var conditionMode = (dataset.conditionMode || 'AND').toUpperCase();
    var stopAfterMatch = dataset.stopAfterMatch === 'true';

    if (debugMode) {
      console.log('─────────────────────────────────────');
      console.log('[QR Router] Section ' + sectionIndex + ' 开始执行');
      console.log('[QR Router] 条件模式: ' + conditionMode);
      console.log('[QR Router] 目标URL: ' + targetUrl);
      console.log('[QR Router] 默认URL: ' + defaultUrl);
      console.log('[QR Router] 匹配后停止: ' + stopAfterMatch);
    }

    // 收集该 Section 下所有 Block 条件
    var blockEls = sectionEl.querySelectorAll('[data-qr-block]');

    if (blockEls.length === 0) {
      if (debugMode) {
        console.log('[QR Router] Section ' + sectionIndex + ': 没有条件 Block，跳过。');
      }
      return false;
    }

    // 评估所有 Block 条件
    var conditionResults = [];
    for (var i = 0; i < blockEls.length; i++) {
      var blockEl = blockEls[i];
      var blockType = blockEl.dataset.blockType || '';
      // 解析 Block 配置（JSON 格式存在 data-block-config 属性中）
      var blockConfig = {};
      try {
        blockConfig = JSON.parse(blockEl.dataset.blockConfig || '{}');
      } catch (e) {
        if (debugMode) {
          console.warn('[QR Router] Section ' + sectionIndex + ' Block ' + i + ': 配置解析失败', e);
        }
      }
      var result = evaluateCondition(blockType, blockConfig, debugMode, sectionIndex, i);
      conditionResults.push(result);
    }

    // 根据 AND / OR 模式计算最终结果
    var conditionMet = false;
    if (conditionMode === 'OR') {
      // OR 模式：任意一个为 true 即满足
      conditionMet = conditionResults.some(function (r) { return r === true; });
    } else {
      // AND 模式（默认）：所有条件都为 true 才满足
      conditionMet = conditionResults.length > 0 && conditionResults.every(function (r) { return r === true; });
    }

    if (debugMode) {
      console.log('[QR Router] Section ' + sectionIndex + ' 条件结果: ' + conditionResults.join(', '));
      console.log('[QR Router] Section ' + sectionIndex + ' 最终判断: ' + (conditionMet ? '✅ 条件满足' : '❌ 条件不满足'));
    }

    if (conditionMet) {
      // 条件满足：跳转到 target_url
      if (targetUrl) {
        var redirected = safeRedirect(targetUrl, debugMode, sectionIndex);
        if (redirected) {
          return true; // 跳转触发，停止执行
        }
      }
      // 如果 stop_after_match = true 且即使路径相同也停止后续
      if (stopAfterMatch) {
        if (debugMode) {
          console.log('[QR Router] Section ' + sectionIndex + ': stop_after_match=true，终止后续 Section 执行。');
        }
        return true;
      }
    } else {
      // 条件不满足：跳转到 default_url（如果配置了的话）
      if (defaultUrl) {
        var defaultRedirected = safeRedirect(defaultUrl, debugMode, sectionIndex);
        if (defaultRedirected) {
          return true; // 跳转触发，停止执行
        }
      }
    }

    return false;
  }

  // ============================================================
  // 主执行入口
  // 在 DOMContentLoaded 后按 DOM 顺序执行所有 QR Router Section
  // ============================================================
  function runQrRouter() {
    // ============================================================
  // 后台编辑器环境检测
  // Shopify 主题编辑器通过 iframe 加载页面，且 URL 带有特征参数
  // 检测到任意一种情况则直接退出，不执行任何跳转
  // ============================================================
  var isInIframe = window.self !== window.top;
  var isShopifyEditor = window.location.search.indexOf('?preview_theme_id') !== -1
    || window.location.search.indexOf('preview_theme_id') !== -1
    || window.location.search.indexOf('_ab=') !== -1
    || typeof window.Shopify !== 'undefined' && window.Shopify.designMode === true;

  if (isInIframe || isShopifyEditor) {
    console.log('[QR Router] 检测到 Shopify 后台编辑器环境，跳转已禁用。');
    return;
  }
    // 查找页面中所有 QR Router Section，按 DOM 顺序执行
    var sections = document.querySelectorAll('[data-qr-router-section]');

    if (sections.length === 0) {
      return; // 页面中没有 QR Router Section，直接退出
    }

    for (var i = 0; i < sections.length; i++) {
      var triggered = processSection(sections[i], i + 1);
      if (triggered) {
        // 跳转已触发，停止后续 Section 执行
        break;
      }
    }
  }

  // ============================================================
  // 绑定执行时机：DOMContentLoaded
  // 仅执行一次，不使用 setInterval，不监听 DOM 变化
  // ============================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runQrRouter);
  } else {
    // DOM 已就绪（脚本异步加载情况）
    runQrRouter();
  }

})();